import React, { useEffect, useMemo, useRef } from 'react';
import Map, { Layer, MapRef, Source } from 'react-map-gl';
import { getAreaCenter, parseAreaGeoJson } from '../utils/areaGeoJson';
import { getMapStyle } from '../utils/mapboxStyle';
import NexradOverlay from './NexradOverlay';
import { normalizeHighlightColor } from '../utils/highlightColor';
import { MAPBOX_TOKEN } from '../utils/mapboxToken';

interface Props {
  value?: string | null;
  highlightColor?: string | null;
}


function normalizeColor(value?: string | null) {
  return normalizeHighlightColor(value);
}

const DiscussionAreaViewer: React.FC<Props> = ({ value, highlightColor }) => {
  const mapRef = useRef<MapRef | null>(null);

  const parsedState = useMemo(() => {
    try {
      return { points: parseAreaGeoJson(value), error: null };
    } catch (error: any) {
      return { points: [], error: error?.message || 'Invalid GeoJSON' };
    }
  }, [value]);

  const bounds = useMemo((): [[number, number], [number, number]] | null => {
    if (parsedState.points.length < 3) return null;
    const lngs = parsedState.points.map(([lng]) => lng);
    const lats = parsedState.points.map(([, lat]) => lat);
    return [
      [Math.min(...lngs), Math.min(...lats)],
      [Math.max(...lngs), Math.max(...lats)],
    ];
  }, [parsedState.points]);

  useEffect(() => {
    if (!bounds) return;
    const map = mapRef.current?.getMap();
    if (!map) return;
    map.fitBounds(bounds, { padding: 60, maxZoom: 9, duration: 0 });
  }, [bounds]);

  const center = getAreaCenter(parsedState.points);
  const activeColor = normalizeColor(highlightColor);

  if (!value?.trim()) return null;
  if (parsedState.points.length < 3) {
    return <div className="module-note">Area of concern geometry is unavailable.</div>;
  }

  const polygonCoordinates = [...parsedState.points, parsedState.points[0]];

  return (
    <div className="area-viewer-stack">
      <div className="area-map-shell">
        <Map
          key={document.body.dataset.theme === 'light' ? 'light-map' : 'dark-map'}
          ref={mapRef}
          initialViewState={{ latitude: center[0], longitude: center[1], zoom: 7 }}
          mapStyle={getMapStyle()}
          mapboxAccessToken={MAPBOX_TOKEN}
          style={{ width: '100%', height: 320 }}
          reuseMaps
        >
          <NexradOverlay />
          <Source
            id="discussion-viewer-polygon"
            type="geojson"
            data={{
              type: 'Feature',
              geometry: { type: 'Polygon', coordinates: [polygonCoordinates] },
              properties: {},
            }}
          >
            <Layer
              id="discussion-viewer-polygon-fill"
              type="fill"
              paint={{ 'fill-color': activeColor, 'fill-opacity': 0.24 }}
            />
            <Layer
              id="discussion-viewer-polygon-line"
              type="line"
              paint={{ 'line-color': activeColor, 'line-width': 2 }}
            />
          </Source>
        </Map>
      </div>
      {parsedState.error ? <p className="field-help">{parsedState.error}</p> : null}
    </div>
  );
};

export default DiscussionAreaViewer;